// StatePicker.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Image } from 'react-bootstrap';
import { Check2, ChevronDown, GeoAlt } from 'react-bootstrap-icons';
import { useTranslation } from 'react-i18next';

import { STATE_OPTIONS } from '../../../utils/homeLocation';
import './Parties.css';

/**
 * StatePicker
 *
 * Which state the party page is showing. A feed button that opens a filterable
 * list, for the same reasons the elections panel's PillSelect is not a native
 * <select>: an OS picker ignores the app's fonts and cannot carry a flag.
 *
 * Browsing only. The caller puts the choice in the URL and never writes it to
 * the saved location, so looking at Ohio's Greens does not move the reader.
 *
 * Two lessons carried over from PillSelect:
 *
 *   - The filter is focused only with a fine pointer. On touch, focusing an
 *     input opens the keyboard over the list, and iOS zooms into any input
 *     under 16px, which is why the filter is 16px.
 *   - The list is scrolled to the current state by hand, not scrollIntoView,
 *     which also scrolls the page and jumps it out from under a thumb.
 */

const hasFinePointer = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(pointer: fine)').matches;

function StatePicker({ value, onChange, placeholder }) {
  const { t } = useTranslation('common');
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const wrapperRef = useRef(null);
  const filterRef = useRef(null);
  const listRef = useRef(null);
  const selectedRef = useRef(null);

  const selected = useMemo(
    () => STATE_OPTIONS.find((option) => option.abbr === value) || null,
    [value]
  );

  const visible = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    if (!needle) return STATE_OPTIONS;
    return STATE_OPTIONS.filter(
      (option) =>
        option.name.toLowerCase().includes(needle) || option.abbr.toLowerCase() === needle
    );
  }, [filter]);

  useEffect(() => {
    if (!open) {
      setFilter('');
      return undefined;
    }

    const onDocumentDown = (event) => {
      if (!wrapperRef.current?.contains(event.target)) setOpen(false);
    };
    const onKey = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', onDocumentDown);
    document.addEventListener('touchstart', onDocumentDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocumentDown);
      document.removeEventListener('touchstart', onDocumentDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;

    if (hasFinePointer()) filterRef.current?.focus();

    const list = listRef.current;
    const item = selectedRef.current;
    if (!list || !item) return;

    const listBox = list.getBoundingClientRect();
    const itemBox = item.getBoundingClientRect();
    list.scrollTop += itemBox.top - listBox.top - (list.clientHeight - item.clientHeight) / 2;
  }, [open]);

  const choose = (abbr) => {
    setOpen(false);
    if (abbr !== value) onChange(abbr);
  };

  return (
    <div className="party-state-picker feed-item" ref={wrapperRef}>
      <Button
        className="clickable party-state-toggle"
        id="billLink"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t('common:state', 'State')}
        onClick={() => setOpen((current) => !current)}
      >
        <GeoAlt size={13} className="me-1" aria-hidden />
        {selected?.flag && (
          <Image
            src={selected.flag}
            alt=""
            className="party-state-flag me-1"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
          />
        )}
        <span>{selected ? selected.name : placeholder}</span>
        <ChevronDown size={12} className="ms-1" aria-hidden />
      </Button>

      {open && (
        <div className="party-state-menu">
          <input
            ref={filterRef}
            type="text"
            className="party-state-filter"
            value={filter}
            placeholder={t('common:filter', 'Filter')}
            onChange={(e) => setFilter(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && visible.length) choose(visible[0].abbr);
            }}
          />

          <ul role="listbox" className="party-state-list" ref={listRef}>
            {visible.map((option) => {
              const isSelected = option.abbr === value;
              return (
                <li key={option.key || option.abbr}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    ref={isSelected ? selectedRef : null}
                    className={`party-state-option ${
                      isSelected ? 'party-state-option--selected' : ''
                    }`}
                    onClick={() => choose(option.abbr)}
                  >
                    <span className="party-state-option-label">
                      {/* Fixed slot: DC and the territories have no flag, and
                          without reserved space their names sit out of line. */}
                      <span className="party-state-flag-slot">
                        {option.flag && (
                          <Image
                            src={option.flag}
                            alt=""
                            className="party-state-flag"
                            onError={(e) => {
                              e.currentTarget.style.visibility = 'hidden';
                            }}
                          />
                        )}
                      </span>
                      {option.name}
                    </span>
                    {isSelected && <Check2 size={15} aria-hidden />}
                  </button>
                </li>
              );
            })}

            {!visible.length && (
              <li className="party-state-empty">
                {t('common:noResultsFound', 'No results found.')}
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

export default StatePicker;